import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	useAlerts,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { SearchNoResults } from "../SearchNoResults";
import {
	getPropertySortText,
	MultiSelectOption,
	NoResults,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	SelectOption
} from "client/Components";
import {
	EMPTY_ARRAY,
	isNilOrWhiteSpace
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { SortDirection } from "client/Api/common";
import {
	CommunityPostGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchCommunityPosts,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	convertSortDirectionToDto,
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { IFormHandler, useFormHandler } from "client/Forms";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { listCommunities } from "client/Api/Communities/community";
import { CommunityViewModel, listAuthors } from "client/Api/Communities/communityPosts";
import { CommunityPostItemView } from "../Items/CommunityPostItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type CommunityPostSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const CommunityPostSearch = (props: CommunityPostSearchProps): ReactElement => {
	const {
		mobileSearchSettingsTarget,
		searchTerm
	} = props;

	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(true);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [communityOptions, setCommunityOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);
	const [authorOptions, setAuthorOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);

	const [formData, setFormData] = useState<CommunityPostsSearchFormData>({
		...defaultFilters,
		sort: defaultSortValue
	});

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<CommunityPostsSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const listHandle: RefObject<ScrollableVirtualListHandle<CommunityPostGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<CommunityPostGlobalSearchResultItem>>(null);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				authorIds: formData.authorIds,
				communityIds: formData.communityIds
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			searchTerm,
			formData.authorIds,
			formData.communityIds,
			formData.sort
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingFilters(true);
					const communitiesRequest: Promise<readonly CommunityViewModel[]> = listCommunities(signal);
					const authorsRequest: Promise<readonly UserIdentityModel[]> = listAuthors(signal);

					const [communities, authors] = await Promise.all([communitiesRequest, authorsRequest]);

					setCommunityOptions(communities.map(convertCommunityToOption));
					setAuthorOptions(authors.map(convertAuthorToOption));
					setIsLoadingFilters(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingFilters(false);
					console.error(e);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={127}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="community-post-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: CommunityPostGlobalSearchResultItem, index: number): ReactElement => (
						<CommunityPostItemView
							key={item.id}
							navigationIndex={index}
							item={item}
							isMobile={isMobile}
							itemScope={communityPostSearchScope}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
					/>

					<SearchSettings.MultiSelect
						label={localize("_Communities")}
						attachable={formHandler.for("communityIds")}
						options={communityOptions}
						placeholder={localize("_SelectCommunity(ies)")}
						hasBusyIndicator={isLoadingFilters}
					/>

					<SearchSettings.MultiSelect
						label={localize("_Author")}
						attachable={formHandler.for("authorIds")}
						options={authorOptions}
						placeholder={localize("_SelectAuthor(s)")}
						hasBusyIndicator={isLoadingFilters}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
CommunityPostSearch.displayName = "CommunityPostSearch";

type CommunityPostsSearchFormData = {
	communityIds: readonly number[];
	authorIds: readonly number[];
	sort: SortValue;
};

const PAGE_SIZE = 20;

type FilterModel = Omit<CommunityPostsSearchFormData, "sort">;

const defaultFilters: FilterModel = {
	authorIds: EMPTY_ARRAY,
	communityIds: EMPTY_ARRAY
};

type FilterContext = {
	communityIds: readonly number[];
	authorIds: readonly number[];
};

const useDataSource = createRemoteDataSource<
	CommunityPostGlobalSearchResultItem,
	FilterContext,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook({
		performRequest(options): Promise<LazyLoadPageResult<CommunityPostGlobalSearchResultItem>> {
			if (isNilOrWhiteSpace(options.searchTerm)) {
				return Promise.resolve({items: [], hasMore: false});
			}

			const {
				amount,
				filterState,
				searchTerm,
				sorting,
				after
			} = options;

			const {
				authorIds,
				communityIds
			} = filterState;

			return searchCommunityPosts(
				{
					searchTerm,
					authorIds,
					communityIds,
					amount,
					sortDirection: convertSort(sorting),
					pagingInfo: after
						? {
							isSet: true,
							value: {
								id: after.id,
								createdOn: after.createdOn
							}
						}
						: { isSet: false }
				},
				options.signal
			);
		},
	});

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<CommunityPostGlobalSearchResultItem>;
};

const defaultSortValue: SortValue = {
	direction: DataSourceSortDirection.Descending,
	propertyName: "createdOn"
};

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: getPropertySortText("_Date", "_NewToOld"),
		value: defaultSortValue,
		valueTrackingId: "created-on-new-old"
	},
	{
		text: getPropertySortText("_Date", "_OldToNew"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "createdOn"
		},
		valueTrackingId: "created-on-old-new"
	}
];

function convertSort(
	sortingState: DataSourceSortableState<CommunityPostGlobalSearchResultItem> | nil
): SortDirection {
	if (!sortingState) {
		return SortDirection.Ascending;
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	switch(sortingState.propertyName) {
		case "createdOn":
			return convertSortDirectionToDto(sortingState.direction);
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}
}

function convertCommunityToOption(community: CommunityViewModel): SelectOption<number> {
	return {
		text: community.name,
		value: community.id
	};
}

function convertAuthorToOption(author: UserIdentityModel): SelectOption<number> {
	const {
		computedDisplayName
	} = getComputedDisplayName(author);

	return {
		text: computedDisplayName,
		value: author.userId
	};
}

function getUniqueKey(item: CommunityPostGlobalSearchResultItem): string {
	return `${item.id}`;
}

const communityPostSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.CommunityPosts
};